import type { FC, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useMemo } from 'react';

import { setRUMUserProperty } from '@opendoor/observability';
import { useLocation, useParams } from '@remix-run/react';
import last from 'lodash-es/last';

import {
  OdProtosSellCommon_DenialReason as DenialReason,
  OdProtosSellCommon_Product as Product,
} from '__generated__/globalTypes';

import { LoadingPage } from 'platform/components/LoadingPage';
import {
  DevError,
  hasPermissionDeniedError,
  isInvalidArgumentError,
  SystemError,
  UnauthorizedError,
} from 'platform/Error';
import { useObservability } from 'platform/observability';
import { useNavigate } from 'platform/remix-run/react';
import { parseAddressRoute } from 'platform/routingHelpers';

import type {
  SellerLeadAddressFragment,
  SellerLeadFragment,
} from 'sellDirect/graphql/fragments/__generated__/SellerLeadFragments';

import { useSellerJourney } from './useSellerJourney';
import type { SellerCustomerLead } from './useSellerLeadsByCustomer';
import { useSellerLeadsByCustomer } from './useSellerLeadsByCustomer';

export type SellerLead = Omit<SellerLeadFragment, 'address'> & {
  address: SellerLeadAddressFragment;
};

const isProductQualified = (productOffering: Product, lead?: SellerLead | null): boolean => {
  if (!lead || !lead.productOfferings || lead.productOfferings.length === 0) {
    return false;
  }

  const offering = lead.productOfferings.find(({ product }) => product === productOffering);
  return offering?.denialReason === DenialReason.NO_DENIAL;
};

export type ISellerLeadContext = {
  lead: SellerLead | null;
  customerLeads: SellerCustomerLead[];
  sellerInputUUID: string;
  isBuyOnlyCustomer: boolean;
  isOpendoorOfferQualified: boolean;
  loading: boolean;
};

/** @internal */
export const SellerLeadContext = createContext<ISellerLeadContext>({
  lead: null,
  customerLeads: [],
  sellerInputUUID: '',
  isBuyOnlyCustomer: false,
  isOpendoorOfferQualified: false,
  loading: false,
});

/**
 * Returns the latest seller lead for a given customer and address UUID, or the seller lead for a given seller input UUID.
 * Also returns the calculated seller input UUID, customer UUID, address UUID, and experiment entity ID.
 *
 * @throws Error if no leads are found for the customer and feature flag is enabled for buy-only hub routing
 */
export function useSellerLead() {
  const value = useContext(SellerLeadContext);
  if (!value) {
    throw new DevError('SellerLeadContext missing');
  }
  return value;
}

/**
 * SellerLeadProvider is a wrapper component that provides the latest seller lead for a given customer and address UUID.
 *
 * Renders an error page if the customer doesn't have adequate permissions to view the lead.
 * @throws UnauthorizedError if the customer doesn't have adequate permissions to view the lead
 */
export const SellerLeadProvider: FC<PropsWithChildren> = ({ children }) => {
  const { trackEvent } = useObservability();
  const navigate = useNavigate();
  const { addressUUID } = useParams();
  const { pathname } = useLocation();
  const { basePath, subPath } = parseAddressRoute(pathname);

  const sellerLeadsByCustomer = useSellerLeadsByCustomer();
  const sellerJourneyResponse = useSellerJourney();

  const sellerLeadData = useMemo<ISellerLeadContext>(() => {
    let customerLeads = sellerLeadsByCustomer.customerLeads;
    const lead = sellerJourneyResponse.lead;

    if (lead) {
      // It's possible for Reception, Seller, and Web to be out of sync and the
      // current lead to not be included in the list of seller leads. If that
      // happens, we'll add it into the list ourselves.
      const isCurrentLeadInAllLeads = customerLeads.some(
        (currentLead) => currentLead.address.uuid === lead.address?.uuid,
      );
      if (!isCurrentLeadInAllLeads) {
        customerLeads = [...customerLeads, lead];
      }
    }

    const isBuyOnlyCustomer = customerLeads.length === 0;
    const isSellDirectQualified = isProductQualified(Product.OPENDOOR_DIRECT, lead);
    const isSellWithUpsideQualified = isProductQualified(Product.SELL_WITH_UPSIDE, lead);
    const isOpendoorOfferQualified = isSellDirectQualified || isSellWithUpsideQualified;

    return {
      loading: sellerLeadsByCustomer.loading || sellerJourneyResponse.loading,
      lead: lead ?? null,
      customerLeads,
      isBuyOnlyCustomer,
      sellerInputUUID: lead?.source?.id || '',
      isOpendoorOfferQualified,
    };
  }, [
    sellerLeadsByCustomer.loading,
    sellerLeadsByCustomer.customerLeads,
    sellerJourneyResponse.loading,
    sellerJourneyResponse.lead,
  ]);

  const latestCustomerAddressUUID = last(sellerLeadData.customerLeads)?.address.uuid;

  const missingJourneyWithoutError =
    sellerJourneyResponse.sellerJourney == null && !sellerJourneyResponse.error;
  const hasInvalidArgumentError =
    sellerJourneyResponse.error && isInvalidArgumentError(sellerJourneyResponse.error);

  // If either of these conditions are true, we will try to redirect to the customer's latest address
  const shouldAttemptLatestLeadAddressRecovery =
    missingJourneyWithoutError || hasInvalidArgumentError;

  // Redirect to latest address if current address is invalid or no address is provided
  useEffect(() => {
    if (sellerLeadData.loading) {
      return;
    }

    if (!addressUUID && latestCustomerAddressUUID) {
      navigate(`${basePath}/${latestCustomerAddressUUID}${subPath}`);
    } else if (
      shouldAttemptLatestLeadAddressRecovery &&
      latestCustomerAddressUUID &&
      addressUUID !== latestCustomerAddressUUID
    ) {
      navigate(`${basePath}/${latestCustomerAddressUUID}${subPath}`);
    }
  }, [
    basePath,
    subPath,
    navigate,
    latestCustomerAddressUUID,
    addressUUID,
    sellerLeadData.loading,
    shouldAttemptLatestLeadAddressRecovery,
  ]);

  /**
   * This happens sometimes but shouldn't block the rendering of the UI. Just tracking it so we can monitor the frequency for now
   */
  useEffect(() => {
    if (
      !sellerLeadData.loading &&
      sellerLeadData.lead &&
      (!sellerLeadData.sellerInputUUID || sellerLeadData.sellerInputUUID.trim() === '')
    ) {
      trackEvent('missing-seller-input');
    }
  }, [sellerLeadData.lead, sellerLeadData.loading, sellerLeadData.sellerInputUUID, trackEvent]);

  useEffect(() => {
    if (sellerLeadData?.sellerInputUUID) {
      setRUMUserProperty({ sellerInputUUID: sellerLeadData.sellerInputUUID });
    }
  }, [sellerLeadData.sellerInputUUID]);

  if (!sellerLeadData.loading) {
    if (sellerJourneyResponse.error) {
      if (hasPermissionDeniedError(sellerJourneyResponse.error)) {
        throw new UnauthorizedError('LatestSellerLead', 'permission-denied', 'seller');
      }

      if (shouldAttemptLatestLeadAddressRecovery) {
        // If no fallback address exists, or the current address IS the fallback, throw error
        if (!latestCustomerAddressUUID || addressUUID === latestCustomerAddressUUID) {
          throw new SystemError(sellerJourneyResponse.error, 'seller');
        }

        // Show loading while redirect effect (above) navigates to fallback address
        return <LoadingPage />;
      }

      throw new SystemError(sellerJourneyResponse.error, 'seller');
    }

    // Show loading while redirect effect navigates to the latest address.
    // If the UUID is already in the URL but useParams() still doesn't have it, the route
    // is missing the :addressUUID param — that's a routing misconfiguration.
    if (!addressUUID && latestCustomerAddressUUID) {
      if (pathname.includes(latestCustomerAddressUUID)) {
        throw new DevError('SellerLeadProvider mounted on route without :addressUUID param');
      }
      return <LoadingPage />;
    }

    // avoid directing the customer to the buy only experience if we fail to load customer leads
    if (sellerLeadsByCustomer.error) {
      if (hasPermissionDeniedError(sellerLeadsByCustomer.error)) {
        throw new UnauthorizedError('ListLeadsByCustomer', 'permission-denied', 'seller');
      }
      throw new SystemError(sellerLeadsByCustomer.error, 'seller');
    }
  }

  return <SellerLeadContext.Provider value={sellerLeadData}>{children}</SellerLeadContext.Provider>;
};
